import { LitElement, html, nothing } from 'lit';
import { customElement, property, query, queryAll } from 'lit/decorators.js';
import { map } from 'lit/directives/map.js';
import { when } from 'lit/directives/when.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { dates, Panel } from '@omegagrid/core';
import { msg, getLocale } from '@omegagrid/localize';
import { SerializedValue, TreeDropdown } from '@omegagrid/tree';
import rp, { Service } from '@reporting/rp-app-host';
import { AccountingRights } from '@reporting/rp-types/core';
import { ETLJob } from '@reporting/rp-types/jobs';
import { EntityAccessType } from '@reporting/rp-types/users';
import { getParamTreeAdapter, hasParamList } from '../adapters/jobParamTreeAdapter';
import { CellColor, style } from './jobWatcherInfo.style';
import { formatSeconds } from '../helpers';
import { JobStatusExt } from '../types';

const formatDateTime = (value: string) => dates.getDateFormatter(getLocale(), dates.getDateFormat({ withTime: true }))(value);
const EMPTY_DESCRIPTION_REGEX = /^no description/i;

/** Job parameters in the display order; each may take its values from a list thread (id_thread_*). */
const PARAM_NAMES = ['value_i', 'value_b', 'value_f', 'value_s', 'value_s2', 'value_s3'] as const;
type ParamName = typeof PARAM_NAMES[number];

type Outcome = { icon: string, color: CellColor };
/** msdb.dbo.sysjobservers.last_run_outcome: 0 = failed, 1 = succeeded, 3 = canceled, 5 = unknown */
const OUTCOMES: Record<number, Outcome> = {
	0: { icon: 'circle-xmark', color: 'red' },
	1: { icon: 'circle-check', color: 'green' },
	3: { icon: 'circle-stop', color: 'yellow' },
};
const UNKNOWN_OUTCOME: Outcome = { icon: 'circle-question', color: 'gray' };

type ActionButton = { icon: string, color: string, text: string, action?: () => void };
type InfoRow = { label: string, value: unknown, color?: CellColor };

@customElement('rp-app-jobwatcherinfo')
export class JobWatcherInfo extends LitElement {

	static styles = [style];

	@property({type: Object})
	job: ETLJob;

	@property({type: Object, hasChanged(newVal: JobStatusExt, oldVal: JobStatusExt) {
		return newVal?.running !== oldVal?.running
			|| newVal?.running_job_name !== oldVal?.running_job_name
			|| newVal?.last_run !== oldVal?.last_run
			|| newVal?.last_run_end !== oldVal?.last_run_end;
	}})
	jobStatus: JobStatusExt;

	@property({type: String})
	state: 'starting'|'stoping'|'running'|'idle' = 'idle';

	@property({type: Object})
	etlService: Service;

	@property({type: Object})
	jobService: Service;

	@query('#timer')
	timer: HTMLDivElement;

	@query('og-panel')
	panel: Panel;

	@queryAll('og-tree-dropdown')
	treeDropdowns: NodeListOf<TreeDropdown & {_initialized: boolean}>;

	private _paramValues: Record<string, SerializedValue[]> = {};

	/** Parameters the job takes from a list (a list thread, the lists of a default job). */
	get listParams() {
		return PARAM_NAMES.filter(param => hasParamList(this.job, param));
	}

	get canRun() {
		return this.job.allow || rp.userProvider.hasRight([AccountingRights.Admin, AccountingRights.JobsDefine]);
	}

	get canPlan() {
		return !this.job.default_flag && rp.userProvider.hasRight([AccountingRights.JobsRead, AccountingRights.JobsDefine]);
	}

	get isAdmin() {
		return rp.userProvider.hasRight(AccountingRights.Admin);
	}

	willUpdate(changedProperties: Map<PropertyKey, unknown>) {
		if (changedProperties.has('job')) {
			this._paramValues = {};
		}
	}

	start() {
		this.dispatchEvent(new CustomEvent('start'));
	}

	stop() {
		this.dispatchEvent(new CustomEvent('stop'));
	}

	get values() {
		const values: Record<string, string> = {};
		this.treeDropdowns.forEach(dd => {
			if (dd.value && dd.value.length) values[dd.dataset['name']] = dd.value[0][0];
		});
		return values;
	}

	updated() {
		// workaround to prevent job dropdown from closing after clicking on parameter dropdown
		this.treeDropdowns.forEach(dd => {
			if (dd._initialized) return;
			dd._initialized = true;
			dd.updateComplete.then(() => dd.dropdown.addEventListener('mousedown', e => e.stopPropagation()));
		});
	}

	updateTimer() {
		if (!this.timer) return;
		if (this.jobStatus?._start) {
			const elapsed = (Date.now() / 1000) - this.jobStatus._start;
			this.timer.innerText = formatSeconds(elapsed);
		} else {
			this.timer.innerText = '';
		}
	}

	getParamAdapter(param: ParamName) {
		return getParamTreeAdapter({ etl: this.etlService, jobs: this.jobService }, this.job, param);
	}

	/**
	 * The dropdown shows the bare key of a value whose list it has not loaded yet, so the default
	 * value gets its label from the list itself (fetched on the first expand, see loadParamLabels()).
	 */
	getParamValue(param: ParamName): SerializedValue[] {
		if (param in this._paramValues) return this._paramValues[param];
		const key = this.job.params?.[param];
		if (key == null) return [];
		const label = this.getParamAdapter(param).getLabel(key);
		return [label == null ? [key as string, 0, 1] : [key as string, 0, 1, { v: label }]];
	}

	/** Fetches the lists of the parameters with a default value so getParamValue() can label them. */
	loadParamLabels() {
		const pending = this.listParams
			.filter(param => this.job.params?.[param] != null)
			.map(param => this.getParamAdapter(param))
			.filter(adapter => !adapter.loaded);
		if (pending.length === 0) return;
		Promise.all(pending.map(adapter => adapter.getTree()))
			.catch(e => console.error('Job parameter list load failed', e))
			.then(() => this.requestUpdate());
	}

	_onPanelResizeStart = () => {
		if (!this.panel.collapsed) this.loadParamLabels();
	}

	openPlans() {
		rp.host.open(
			`jobs-plan-${btoa(this.job.job_name)}`,
			{ title: `${msg('Plans')}: ${this.job.title}` }
		);
	}

	openAccess(e: PointerEvent) {
		e.stopImmediatePropagation();
		rp.host.open(
			`users-entityUsers-${EntityAccessType.Job}-${this.job.job_name}`,
			{ title: `${msg('Access')}: ${this.job.job_name}` }
		);
	}

	/** Start when idle, stop when running, a disabled hourglass while a start/stop request is pending. */
	get actionButton(): ActionButton {
		switch (this.state) {
			case 'starting': return { icon: 'hourglass', color: 'yellow', text: msg('Starting') };
			case 'stoping': return { icon: 'hourglass', color: 'yellow', text: msg('Stoping') };
		}
		return this.jobStatus.running
			? { icon: 'stop', color: 'red', text: msg('Stop'), action: () => this.stop() }
			: { icon: 'play', color: 'green', text: msg('Start'), action: () => this.start() };
	}

	render = () => html`
		<og-panel collapsible collapsed @resizestart="${this._onPanelResizeStart}">
			<div class="header" slot="header">
				<div class="job-info">
					<div class="job-icon">
						${this.jobStatus?.running
							? html`<og-loader icon="refresh"></og-loader>`
							: html`<og-icon icon="gears"></og-icon>`}
					</div>
					<div class="job-title" title="${msg(this.job.title)}">${msg(this.job.title)}</div>
				</div>
				<div id="timer"></div>
			</div>
			<div class="body" slot="body">
				${this.jobStatus ? html`
					<table>
						${this.renderDescription()}
						${this.renderControls()}
						${this.renderStatus()}
					</table>
				` : html`<div style="padding: 4px;">${msg('No job info available.')}</div>`}
			</div>
		</og-panel>
	`;

	renderDescription() {
		const description = this.jobStatus.description;
		return when(description && !EMPTY_DESCRIPTION_REGEX.test(description), () => html`
			<tr><td class="description" colspan="2">${description}</td></tr>
		`);
	}

	/** The action buttons and the parameter dropdowns - only for a user allowed to run the job. */
	renderControls() {
		if (!this.canRun) return nothing;
		const button = this.actionButton;
		return html`
			<tr>
				<td colspan="2">
					<div class="job-buttons">
						<og-button
							class="action"
							icon="${button.icon}"
							color="${button.color}"
							.text="${button.text}"
							?disabled="${!button.action}"
							@click="${button.action}">
						</og-button>
						${when(this.canPlan, () => html`
							<og-button icon="calendar-days" .title="${msg('Plans')}" @click="${this.openPlans}"></og-button>
						`)}
						${when(this.isAdmin, () => html`
							<og-button icon="shield-check" .title="${msg('Access')}" @click="${this.openAccess}"></og-button>
						`)}
					</div>
				</td>
			</tr>
			${map(this.listParams, param => html`
				<tr>
					<td>${msg(this.job[`description_${param}`])}</td>
					<td>
						<og-tree-dropdown
							data-name="${param}"
							.adapter="${this.getParamAdapter(param)}"
							.value="${this.getParamValue(param)}"
							@change="${(e: Event) => { this._paramValues[param] = (e.currentTarget as TreeDropdown).value; }}">
						</og-tree-dropdown>
					</td>
				</tr>
			`)}
		`;
	}

	/** The last/next run info; a row without a value is left out. */
	renderStatus() {
		if (this.job.default_flag) return nothing;
		const status = this.jobStatus;
		const outcome = OUTCOMES[status.last_run_outcome] ?? UNKNOWN_OUTCOME;
		const rows: InfoRow[] = [
			{ label: msg('Last start'), value: status.last_run && formatDateTime(status.last_run) },
			{ label: msg('Last end'), value: status.last_run_end && formatDateTime(status.last_run_end) },
			{ label: msg('Last duration'), color: outcome.color, value: status.last_run_duration && html`
				<span class="outcome"><og-icon icon="${outcome.icon}"></og-icon>${formatSeconds(status.last_run_duration)}</span>
			` },
			{ label: msg('Next start'), value: status.next_run && formatDateTime(status.next_run) },
			{ label: msg('User'), value: status.who_run },
			{ label: msg('Running as'), value: status.running_job_name !== this.job.job_name && status.running_job_name },
		];
		return map(rows.filter(row => row.value), row => html`
			<tr>
				<td>${row.label}</td>
				<td data-color="${ifDefined(row.color)}">${row.value}</td>
			</tr>
		`);
	}

}
